import { useCallback, useEffect, useState } from 'react';
import type { ConnectionQualityState } from '../types/connectionQuality';
import { isPoorConnectionQuality } from './connectionQuality/ConnectionQualityHelper';

const POOR_CONNECTION_WARNING_COOLDOWN_MS = 30 * 60 * 1000;
const DISMISSED_AT_KEY = 'chatyme_agent_poor_connection_warning_at';
const PENDING_KEY = 'chatyme_agent_poor_connection_warning_pending';

function isDismissCooldownActive(): boolean {
  try {
    const dismissedAt = Number(sessionStorage.getItem(DISMISSED_AT_KEY) ?? 0);
    if (!dismissedAt) {
      return false;
    }
    return Date.now() - dismissedAt < POOR_CONNECTION_WARNING_COOLDOWN_MS;
  } catch {
    return false;
  }
}

function markWarningDismissed(): void {
  try {
    sessionStorage.setItem(DISMISSED_AT_KEY, String(Date.now()));
  } catch {
    // ignore storage errors
  }
}

function readPendingWarning(): boolean {
  try {
    return sessionStorage.getItem(PENDING_KEY) === 'true';
  } catch {
    return false;
  }
}

function setPendingWarning(pending: boolean): void {
  try {
    if (pending) {
      sessionStorage.setItem(PENDING_KEY, 'true');
    } else {
      sessionStorage.removeItem(PENDING_KEY);
    }
  } catch {
    // ignore storage errors
  }
}

export function usePoorConnectionWarning(
  connectionQuality: ConnectionQualityState,
  message: string
): { poorConnectionWarning: string | null; dismissPoorConnectionWarning: () => void } {
  const [visible, setVisible] = useState(readPendingWarning);

  useEffect(() => {
    if (!isPoorConnectionQuality(connectionQuality)) {
      return;
    }

    if (isDismissCooldownActive()) {
      return;
    }

    setVisible(true);
    setPendingWarning(true);
  }, [connectionQuality]);

  const dismissPoorConnectionWarning = useCallback(() => {
    setVisible(false);
    setPendingWarning(false);
    markWarningDismissed();
  }, []);

  return {
    poorConnectionWarning: visible ? message : null,
    dismissPoorConnectionWarning
  };
};
